import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  Alert,
  Chip,
  Button,
  DialogActions,
  colors,
  Divider,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listConsumableItems,
  ConsumableItem,
  ConsumableBuyAndSellRateType,
  deleteConsumableItem,
} from 'api/consumablesApi';
import React, { useState } from 'react';
import ConfirmDeleteDialog from 'components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import CloseIcon from '@mui/icons-material/Close';
import {
  groupedConsumableBuyItems,
  groupedConsumableSellItems,
} from '../Utils/Utils';
import ConsumableSellAndBuyDetailsViewTable from './ConsumableSellAndBuyDetailsViewTable';
import StraightenIcon from '@mui/icons-material/Straighten';
import DeleteIcon from '@mui/icons-material/Delete';
import theme from 'theme';
import EditIcon from '@mui/icons-material/Edit';
import AddOrEditConsumableItems from './AddOrEditConsumableItems';

export interface DialogState {
  isBuyRateFormOpen: boolean;
  isSellRateFormOpen: boolean;
  isDetailsViewOpen: boolean;
  editRateData: ConsumableBuyAndSellRateType | null;
  openDetailView?: boolean;
}
interface Props {
  open: boolean;
  selectedItemId?: string;
  handleConsumableClose?: () => void;
}

function ConsumableDetailsView({
  selectedItemId,
  handleConsumableClose,
  open,
}: Props) {
  const queryClient = useQueryClient();
  const [editConsumableItemData, setEditConsumableItemData] =
    useState<ConsumableItem | null>(null);
  const consumableList = useQuery(['consumables-items'], listConsumableItems);
  const consumable = consumableList.data?.find(
    (item) => item?.id.toString() === selectedItemId
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ConsumableItem | null>(null);

  const deleteConsumableItems = useMutation(deleteConsumableItem, {
    onSuccess: () => {
      queryClient.invalidateQueries(['consumables-items']);
    },
    onError: (error) => {
      console.error('Error deleting consumable item:', error);
    },
  });

  const handleDelete = async () => {
    if (!selectedItem) return;
    deleteConsumableItems.mutate(selectedItem.id);
    setDeleteDialogOpen(false);
    setSelectedItem(null);
    handleConsumableClose?.();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleConsumableClose}
        maxWidth="lg"
        fullWidth>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px',
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 10,
            borderBottom: '1px solid #ddd',
          }}>
          <DialogTitle>
            {consumable?.name} |
            <Chip
              key={`${consumable?.id}-chip`}
              label={consumable?.unitType}
              sx={{
                margin: '2px',
                padding: '4px 4px',
                color: '#fff',
                // backgroundColor: consumable?.isDeleted ? red[200] : green[200],
                paddingInline: '0.5rem',
                paddingY: '1rem',
                marginLeft: '1rem',
              }}
              color="primary"
              variant="filled"
              size="small"
              icon={<StraightenIcon />}
            />
          </DialogTitle>
          <IconButton
            onClick={() => {
              handleConsumableClose?.();
            }}>
            <CloseIcon />
          </IconButton>
        </div>
        <div
          style={{
            display: 'flex',
            gap: '2rem',
            padding: '2rem',
            width: '100%',
            overflowY: 'scroll',
          }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ConsumableSellAndBuyDetailsViewTable
              consumable={groupedConsumableBuyItems(consumable)}
              type="buy"
              handleConsumableClose={handleConsumableClose}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ConsumableSellAndBuyDetailsViewTable
              consumable={groupedConsumableSellItems(consumable)}
              type="sell"
              handleConsumableClose={handleConsumableClose}
            />
          </div>
        </div>
        <Box sx={{ p: 2 }}>
          {deleteDialogOpen && selectedItem && (
            <ConfirmDeleteDialog
              open={deleteDialogOpen}
              title="Delete Confirmation"
              content={
                <>
                  Are you sure you want to delete the item{' '}
                  <b>{selectedItem?.name}</b>?
                  <Alert severity="warning" style={{ marginTop: '1rem' }}>
                    This action is irreversible and will permanently delete the
                    data.
                  </Alert>
                </>
              }
              deleteFunc={async () => {
                await handleDelete();
              }}
              handleReject={() => {
                setDeleteDialogOpen(false);
                setSelectedItem(null);
              }}
              handleClose={() => {
                setDeleteDialogOpen(false);
                setSelectedItem(null);
              }}
            />
          )}
        </Box>
        {editConsumableItemData && (
          <AddOrEditConsumableItems
            openAddConsumable={!!editConsumableItemData}
            handleConsumableClose={() => setEditConsumableItemData(null)}
            editConsumableItemData={editConsumableItemData}
          />
        )}
        <Divider />
        <DialogActions sx={{ padding: '1.5rem' }}>
          <Button
            onClick={handleConsumableClose}
            sx={{ color: colors.grey[600], mr: 1 }}>
            Cancel
          </Button>
          {!consumable?.isDeleted &&
            consumable.buyRates?.length === 0 &&
            consumable.sellRates?.length === 0 && (
              <Button
                onClick={() => {
                  setEditConsumableItemData(consumable || null);
                }}
                disableElevation
                startIcon={<EditIcon />}
                variant="contained"
                sx={{
                  marginRight: 1,
                }}>
                Edit Consumable
              </Button>
            )}

          {!consumable.isDeleted && (
            <Button
              onClick={() => {
                setDeleteDialogOpen && setDeleteDialogOpen(true);
                setSelectedItem &&
                  setSelectedItem(consumable as ConsumableItem);
              }}
              disableElevation
              startIcon={<DeleteIcon />}
              variant="contained"
              sx={{
                color: 'white',
                backgroundColor: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              }}>
              Delete Consumable
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ConsumableDetailsView;
